import React, { useMemo, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { AdminOrganizationLocation, Maybe, ParticipantImportResult, ParticipantImportRowStatus } from '@/common/types';
import { DragAndDropWrapper } from '@/components/Patients/AddPatient/styles';
import { Button } from '@/components/common/button/button';
import { resultIsError } from '@/services/HttpService';

const PARTICIPANT_CSV_TEMPLATE = 'first_name,last_name,dob,reference_id,existing_participant_uid,location_name\n';

interface ImportParticipantsModalProps {
    organizationId: number;
    location: AdminOrganizationLocation;
    onClose: () => void;
}

export const ImportParticipantsModal = (props: ImportParticipantsModalProps) => {
    const { organizationId, location, onClose } = props;
    const { httpService } = useHttpContext();
    const { successToast, warningToast } = toasts;

    const [file, setFile] = useState<Maybe<File>>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<Maybe<string>>(undefined);
    const [result, setResult] = useState<Maybe<ParticipantImportResult>>(undefined);

    const templateUrl = useMemo(
        () => URL.createObjectURL(new Blob([PARTICIPANT_CSV_TEMPLATE], { type: 'text/csv' })),
        [],
    );

    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        accept: { 'text/csv': ['.csv'] },
        multiple: false,
        disabled: isSubmitting,
        onDrop: (acceptedFiles) => {
            setError(undefined);
            setFile(acceptedFiles[0]);
        },
    });

    const handleSubmit = async () => {
        if (!file) {
            return;
        }
        setIsSubmitting(true);
        setError(undefined);
        const res = await httpService.importAdminParticipantsForLocation(organizationId, location.id, file);
        if (!res || resultIsError(res)) {
            setError(res?.message || 'Could not import participants.');
            setIsSubmitting(false);
            return;
        }
        setIsSubmitting(false);
        setResult(res);
        if (res.failed === 0) {
            successToast('Participants imported.');
        } else {
            warningToast(`${res.failed} of ${res.total} participants could not be imported.`);
        }
    };

    const failedRows = result?.rows.filter((r) => r.status === ParticipantImportRowStatus.FAILED) ?? [];

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ textAlign: 'center' }}>Import Participants</DialogTitle>
            <DialogContent>
                <Typography variant="body2" component="div" sx={{ mb: 2, fontSize: '16px' }}>
                    <strong>Location: </strong>
                    {location.name}
                </Typography>
                {result ? (
                    <>
                        <Typography variant="body2" sx={{ fontSize: '16px' }}>
                            {result.created} created, {result.updated} updated, {result.failed} failed of {result.total}
                        </Typography>
                        {failedRows.length > 0 && (
                            <Box sx={{ mt: 2, maxHeight: 240, overflowY: 'auto' }}>
                                {failedRows.map((r) => (
                                    <Typography key={r.row} color="error" variant="body2">
                                        Row {r.row}: {r.error}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </>
                ) : (
                    <>
                        <div {...getRootProps()} style={{ outline: 'none' }}>
                            <DragAndDropWrapper
                                $isFocused={isFocused}
                                $isDragAccept={isDragAccept}
                                $isDragReject={isDragReject}
                            >
                                <input {...getInputProps()} />
                                <Button variant="tertiary" onClick={(e) => e.preventDefault()} disabled={isSubmitting}>
                                    {file ? file.name : 'Upload from a CSV file'}
                                </Button>
                            </DragAndDropWrapper>
                        </div>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Please follow the format of our{' '}
                            <Link target="_blank" download="participant_import_template.csv" href={templateUrl}>
                                CSV Template
                            </Link>
                            . first_name and last_name are required, dob must be mm/dd/yyyy or mm/dd.
                        </Typography>
                    </>
                )}
                {error && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {error}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: result ? 'flex-end' : 'space-between' }}>
                {result ? (
                    <Button variant="primary" onClick={onClose}>
                        Close
                    </Button>
                ) : (
                    <>
                        <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting || !file}>
                            Import
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};
